import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const API_BASE = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const SYLLABI = ['CBSE', 'ICSE', 'Kerala State', 'IGCSE', 'Other'];
const MODES = ['online', 'offline'];

const EMPTY = {
  parent_name: '',
  parent_phone: '',
  parent_whatsapp: '',
  parent_email: '',
  student_name: '',
  student_dob: '',
  student_class: '',
  syllabus: '',
  subjects_interested: '',
  mode: '',
  address_line: '',
  post_office: '',
  pincode: '',
  district: '',
  city: '',
  about_student: '',
};

export default function Inquiry() {
  const [form, setForm] = useState(EMPTY);
  const [captchaToken, setCaptchaToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const recaptchaRef = useRef(null);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.parent_name.trim() || !form.parent_phone.trim() || !form.student_name.trim()) {
      setError('Please fill the required fields (marked with *).');
      return;
    }
    if (SITE_KEY && !captchaToken) {
      setError('Please complete the CAPTCHA.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/inquiries/public`, {
        ...form,
        student_dob: form.student_dob || null,
        captcha_token: captchaToken || '',
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not submit. Please try again.');
      recaptchaRef.current?.reset();
      setCaptchaToken('');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={pageStyle}>
        <Decoration />
        <div style={cardStyle}>
          <div style={{ textAlign:'center', position:'relative', zIndex:1 }}>
            <img src="/logo.png" alt="Sapience Edu Connect" style={{ height:'clamp(56px, 14vw, 76px)', marginBottom:16, objectFit:'contain' }} />
            <h1 style={{ fontSize:'clamp(22px, 6vw, 30px)', fontWeight:800, color:'#0f1f45', marginBottom:14 }}>Thank you</h1>
            <p style={{ color:'#475569', fontSize:'clamp(13px, 3.6vw, 15px)', lineHeight:1.6 }}>
              We will create your Sapience profile and share the details soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <Decoration />
      <div style={cardStyle}>
        <div style={{ textAlign:'center', marginBottom:24, position:'relative', zIndex:1 }}>
          <img src="/logo.png" alt="Sapience Edu Connect" style={{ height:'clamp(52px, 12vw, 68px)', marginBottom:10, objectFit:'contain' }} />
          <h1 style={{ fontSize:'clamp(17px, 4.5vw, 21px)', fontWeight:800, color:'#0f1f45', marginBottom:4, lineHeight:1.25 }}>Sapience Edu Connect</h1>
          <p style={{ color:'#64748b', fontSize:'clamp(11.5px, 3vw, 13px)' }}>Connecting minds, shaping futures</p>
          <h2 style={{ fontSize:'clamp(16px, 4.2vw, 19px)', fontWeight:800, color:'#2554b8', marginTop:16, lineHeight:1.25 }}>Student Registration</h2>
        </div>

        {error && (
          <div style={{ padding:'10px 14px', marginBottom:14, borderRadius:10, background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.25)', color:'#dc2626', fontSize:13, fontWeight:600, position:'relative', zIndex:1 }}>
            {error}
          </div>
        )}

        <form onSubmit={submit} style={{ position:'relative', zIndex:1 }}>
          <Section label="Parent / Guardian">
            <Row>
              <Field label="Full name *" value={form.parent_name} onChange={update('parent_name')} required />
              <Field label="Phone *" value={form.parent_phone} onChange={update('parent_phone')} required placeholder="e.g. +91 9XXXXXXXXX" />
            </Row>
            <Row>
              <Field label="WhatsApp number" value={form.parent_whatsapp} onChange={update('parent_whatsapp')} placeholder="Same as phone if no other" />
              <Field label="Email" value={form.parent_email} onChange={update('parent_email')} type="email" />
            </Row>
          </Section>

          <Section label="Student">
            <Row>
              <Field label="Student name *" value={form.student_name} onChange={update('student_name')} required />
              <Field label="Date of birth" value={form.student_dob} onChange={update('student_dob')} type="date" />
            </Row>
            <Row>
              <Field label="Class / Grade" value={form.student_class} onChange={update('student_class')} placeholder="e.g. Class 9" />
              <SelectField label="Syllabus" value={form.syllabus} onChange={update('syllabus')} options={SYLLABI} />
            </Row>
            <Row>
              <SelectField label="Preferred mode" value={form.mode} onChange={update('mode')} options={MODES} />
              <Field label="Subjects interested in" value={form.subjects_interested} onChange={update('subjects_interested')} placeholder="e.g. Maths, Physics" />
            </Row>
          </Section>

          <Section label="Address">
            <Row>
              <Field label="House name / number / street" value={form.address_line} onChange={update('address_line')} placeholder="e.g. Sapience House, MG Road" />
              <Field label="Post office" value={form.post_office} onChange={update('post_office')} />
            </Row>
            <Row>
              <Field label="Pincode" value={form.pincode} onChange={update('pincode')} placeholder="6-digit pincode" />
              <Field label="District" value={form.district} onChange={update('district')} />
            </Row>
            <Row>
              <Field label="City" value={form.city} onChange={update('city')} />
              <div style={{ flex:1, minWidth:180 }} />
            </Row>
          </Section>

          <Section label="About the student (parent voice)">
            <textarea className="inquiry-input" value={form.about_student} onChange={update('about_student')} rows={4}
              placeholder="Tell us about your child — strengths, areas they need support in, learning style, schedule preferences, anything we should know. (Optional)"
              style={{ ...inputStyle, minHeight: 96, resize: 'vertical' }} />
          </Section>

          {SITE_KEY && (
            <div className="inquiry-recaptcha" style={{ marginTop:14, display:'flex', justifyContent:'center' }}>
              <ReCAPTCHA ref={recaptchaRef} sitekey={SITE_KEY} onChange={tok => setCaptchaToken(tok || '')} onExpired={() => setCaptchaToken('')} />
            </div>
          )}

          <button type="submit" disabled={submitting}
            style={{ marginTop:18, width:'100%', padding:'14px 22px', borderRadius:12, border:'none', background: submitting ? '#94a3b8' : 'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:800, cursor: submitting ? 'wait' : 'pointer', fontSize:14, boxShadow:'0 6px 18px rgba(37,84,184,0.35)', letterSpacing:0.3 }}>
            {submitting ? 'Submitting…' : 'Submit Registration'}
          </button>
          <p style={{ marginTop:14, textAlign:'center', fontSize:11.5, color:'#94a3b8' }}>
            By submitting, you agree to be contacted by our team about academic services.
          </p>
        </form>
      </div>
    </div>
  );
}

// ── Branded background — matches the LMS dashboard look ──
function Decoration() {
  return (
    <>
      <div style={{ position:'fixed', top:'-15vw', right:'-15vw', width:'min(380px, 60vw)', height:'min(380px, 60vw)', borderRadius:'50%', background:'radial-gradient(circle, rgba(37,84,184,0.18), transparent 70%)', pointerEvents:'none' }} />
      <div style={{ position:'fixed', bottom:'-20vw', left:'-18vw', width:'min(460px, 70vw)', height:'min(460px, 70vw)', borderRadius:'50%', background:'radial-gradient(circle, rgba(124,58,237,0.15), transparent 70%)', pointerEvents:'none' }} />
      <div style={{ position:'fixed', top:'40%', left:'8%', width:'min(160px, 24vw)', height:'min(160px, 24vw)', borderRadius:'50%', background:'radial-gradient(circle, rgba(59,130,246,0.10), transparent 70%)', pointerEvents:'none' }} />
    </>
  );
}

const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f0f4ff 0%, #e8efff 45%, #ede9fe 100%)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 'clamp(10px, 3vw, 24px)',
  paddingTop: 'clamp(16px, 4vw, 32px)',
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  position: 'relative',
  overflowX: 'hidden',
  boxSizing: 'border-box',
};

const cardStyle = {
  background: 'rgba(255,255,255,0.94)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: 'clamp(14px, 3vw, 20px)',
  padding: 'clamp(20px, 4vw, 36px)',
  maxWidth: 720,
  width: '100%',
  boxShadow: '0 24px 60px rgba(15,31,69,0.14), 0 4px 12px rgba(37,84,184,0.06)',
  border: '1px solid rgba(199,212,245,0.45)',
  position: 'relative',
  zIndex: 1,
  boxSizing: 'border-box',
};

const inputStyle = {
  width: '100%',
  padding: '12px 13px',
  borderRadius: 10,
  border: '1.5px solid #e2e8f0',
  fontSize: 14,             // >=16px on mobile would be ideal but 14 is paired with viewport fix below
  fontFamily: 'inherit',
  outline: 'none',
  background: '#fff',
  color: '#0f1f45',
  boxSizing: 'border-box',
  minHeight: 44,            // comfortable touch target
};

// Inject mobile-only overrides once. iOS auto-zooms when an input's font-size
// is <16px, so we bump it specifically on small viewports. Also keeps the
// reCAPTCHA from causing horizontal scroll on tiny screens.
const MOBILE_CSS = `
@media (max-width: 540px) {
  .inquiry-input { font-size: 16px !important; }
  .inquiry-row { flex-direction: column !important; gap: 12px !important; }
  .inquiry-field { min-width: 0 !important; flex-basis: auto !important; }
  .inquiry-recaptcha { transform: scale(0.88); transform-origin: center top; }
}
`;
let _mobileCssInjected = false;
function ensureMobileCss() {
  if (typeof document === 'undefined' || _mobileCssInjected) return;
  const tag = document.createElement('style');
  tag.setAttribute('data-inquiry-mobile', '1');
  tag.textContent = MOBILE_CSS;
  document.head.appendChild(tag);
  _mobileCssInjected = true;
}
ensureMobileCss();

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="inquiry-row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{children}</div>;
}

function Field({ label, value, onChange, type = 'text', placeholder = '', required = false }) {
  return (
    <div className="inquiry-field" style={{ flex: '1 1 220px', minWidth: 180 }}>
      <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>{label}</label>
      <input className="inquiry-input" type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} style={inputStyle} />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div className="inquiry-field" style={{ flex: '1 1 220px', minWidth: 180 }}>
      <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>{label}</label>
      <select className="inquiry-input" value={value} onChange={onChange} style={inputStyle}>
        <option value="">— Select —</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
